import { type ReactNode } from 'react';

import { useColorRule } from '../../core/color-rule/useColorRule.js';
import { getColorPalette } from '../../core/utils/get-color-palette.js';
import { HistogramDataContext } from '../contexts/HistogramData.context.js';
import { useHistogramColorPalette } from '../hooks/useHistogramColorPalette.js';
import {
  HistogramSeries,
  SeriesMetadata,
  WeakMapSeries,
} from '../types/histogram.js';

interface HistogramChartDataProviderProps {
  children: ReactNode;
  data: HistogramSeries[];
}

const LEFT_SCALE_INDEX = 0;

export function useBuildInitialMetadata(data: HistogramSeries[]) {
  const allUnits = new Set<HistogramSeries['unit']>();
  const colorPalette = useHistogramColorPalette();
  const seriesMap: WeakMapSeries = new WeakMap();

  const ruleColor = useColorRule(data, 'histogram');

  for (let i = 0; i < data.length; i++) {
    const series = data[i];
    const id = `series-${i}`;

    const color =
      ruleColor.for(series) ||
      (series.color
        ? { Default: series.color }
        : getColorPalette(series.name, i, colorPalette));

    allUnits.add(series.unit);

    const metadata: SeriesMetadata = {
      id,
      color,
      scaleIndex: LEFT_SCALE_INDEX,
    };

    seriesMap.set(series, metadata);
  }

  return {
    yUnits: [...allUnits],
    seriesMap,
  };
}

export function safeGet<Y, T extends object>(map: WeakMap<T, Y>, key: T): Y {
  const metadata = map.get(key);

  if (!metadata) {
    throw new Error(`Metadata not found for: ${JSON.stringify(key)}`);
  }

  return metadata;
}

export const HistogramDataProvider = ({
  children,
  data,
}: HistogramChartDataProviderProps) => {
  const { yUnits, seriesMap } = useBuildInitialMetadata(data);
  return (
    <HistogramDataContext.Provider
      value={{
        series: data,
        seriesMap,
        yUnits,
      }}
    >
      {children}
    </HistogramDataContext.Provider>
  );
};

HistogramDataProvider['displayName'] = 'HistogramDataProvider';
